import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Card,
  Divider,
  List,
  Popconfirm,
  ConfigProvider,
  message,
} from "antd";
import { RightCircleTwoTone, LeftCircleTwoTone } from "@ant-design/icons";

import dayjs from "dayjs";
import "dayjs/locale/bg"; // Import Bulgarian locale
import bgBG from "antd/lib/locale/bg_BG";
import localizedFormat from "dayjs/plugin/localizedFormat";

import tableNumberImg from "../../../assets/images/reservationTable.png";
import timeLeftImg from "../../../assets/images/reservationTimeLeft.png";
import guestCountImg from "../../../assets/images/reservationGuests.png";
import reservationsActions from "../../../redux/reservations/actions";

dayjs.extend(localizedFormat);

// Set the locale to Bulgarian
dayjs.locale("bg");

const { editReservation } = reservationsActions;

const ReservationListCards = ({ data }) => {
  const dispatch = useDispatch();
  const [timeRemaining, setTimeRemaining] = useState({});
  const { reservations } = useSelector(
    (state) => state.restaurantReservationsReducer
  );

  const { tables } = useSelector((state) => state.restaurantSchemaReducer);
  function getTimeRemaining(futureDateTime) {
    // Get the current date and time
    const now = new Date();

    // Parse the future date-time string
    const futureDate = new Date(futureDateTime);

    // If the future date is in the past, return '-'
    if (futureDate <= now) {
      return "--:--";
    }

    // Calculate the difference in milliseconds
    const diffMilliseconds = futureDate - now;

    // Convert the difference to hours and minutes
    const diffHours = Math.floor(diffMilliseconds / (1000 * 60 * 60));
    const diffMinutes = Math.floor(
      (diffMilliseconds % (1000 * 60 * 60)) / (1000 * 60)
    );

    // Format the result as HH:mm
    const formattedHours = String(diffHours).padStart(2, "0");
    const formattedMinutes = String(diffMinutes).padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`;
  }

  const checkIfCanSeated = (item) => {
    const currentTime = dayjs();

    const parsedGivenTime = dayjs(
      `${item.reservationDate}T${item.reservationTime}`
    );

    const oneHourBefore = parsedGivenTime.subtract(2, "hour");
    const oneHourAfter = parsedGivenTime.add(2, "hour");

    const isWithinOneHourBeforeOrAfter =
      currentTime.isAfter(oneHourBefore) && currentTime.isBefore(oneHourAfter);

    return isWithinOneHourBeforeOrAfter;
  };

  // Seated reservation for check if we try to seated the same table
  const seatedReservation = reservations.filter((reservation) => {
    return reservation.status === 2;
  });

  const checkIfTableIsCheckedAsBusy = (touchSaleTableIds) => {
    // Extract the IDs from the touchSaleTableIds array
    const touchSaleTableIdList = touchSaleTableIds.map(
      (table) => table.touchSaleTableId
    );

    // Check if there is any table in the touchSaleTableIdList that is busy
    const isAnyTableNotBusy = tables.some(
      (table) =>
        touchSaleTableIdList.includes(table.touchSaleTableId) && table.busy
    );

    // Return the result
    return isAnyTableNotBusy;
  };

  const hasReservationWithAnyTouchSaleTableId = (
    reservations,
    touchSaleTableIds
  ) => {
    // Ensure touchSaleTableIds is an array of IDs
    const touchSaleTableIdList = touchSaleTableIds.map(
      (table) => table.touchSaleTableId
    );

    // Check if any reservation has a matching touchSaleTableId
    return reservations.some((reservation) =>
      reservation.touchSaleTables.some((table) =>
        touchSaleTableIdList.includes(table.touchSaleTableId)
      )
    );
  };

  const handleSeatedReservation = (item) => {
    if (
      hasReservationWithAnyTouchSaleTableId(
        seatedReservation,
        item.touchSaleTables
      ) ||
      checkIfTableIsCheckedAsBusy(item.touchSaleTables)
    ) {
      message.info("Масата е заета");
    } else {
      dispatch(
        editReservation({
          guestsCount: item.guestsCount,
          regionId: item.regionId,
          duration: item.duration,
          reservationTime: item.reservationTime,
          reservationDate: item.reservationDate,
          name: item.name,
          phone: item.phone,
          note: item.note,
          organizationId: parseInt(localStorage.getItem("organizationId")),
          TouchSaleTablesIds: item.touchSaleTables?.map(
            (obj) => obj.touchSaleTableId
          ),
          ReservationId: item.reservationId,
          status: 2,
        })
      );
    }
  };

  const handleReturnToFutureReservation = (item) => {
    dispatch(
      editReservation({
        guestsCount: item.guestsCount,
        regionId: item.regionId,
        duration: item.duration,
        reservationTime: item.reservationTime,
        reservationDate: item.reservationDate,
        name: item.name,
        phone: item.phone,
        note: item.note,
        organizationId: parseInt(localStorage.getItem("organizationId")),
        TouchSaleTablesIds: item.touchSaleTables?.map(
          (obj) => obj.touchSaleTableId
        ),
        ReservationId: item.reservationId,
        status: 1,
      })
    );
    message.success("Успешно преместване на резервацията в Предстоящи");
  };

  // Update the time left to reservations
  useEffect(() => {
    const updateRemainingTime = () => {
      const newTimeRemaining = reservations.reduce((acc, item) => {
        const time = getTimeRemaining(
          item.reservationDate + "T" + item.reservationTime.slice(0, -3)
        );
        acc[item.reservationId] = time; // Assuming each reservation has a unique ID
        return acc;
      }, {});

      setTimeRemaining(newTimeRemaining);
    };

    updateRemainingTime(); // Initial call to set the time remaining
    const intervalId = setInterval(updateRemainingTime, 60000);

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [reservations]);

  return (
    <ConfigProvider locale={bgBG}>
      <List
        dataSource={data}
        renderItem={(item) => (
          <List.Item style={{ borderBlockEnd: "none" }}>
            <Card className="reservations-list-card">
              <div className="reservation-list-inner-container">
                <div
                  className="reservations-detail-main-container"
                  onClick={() =>
                    dispatch({
                      type: "OPEN_RESERVATION_EDIT_MODAL",
                      reservation: item,
                    })
                  }
                >
                  <div className="reservations-detail-rows">
                    <b>{item.name}</b>
                    <b>{item.reservationTime.slice(0, -3)}</b>
                  </div>
                  <div className="reservations-detail-rows">
                    <div className="reservations-detail-container">
                      <img src={guestCountImg} alt="" width="20px" />{" "}
                      {item.guestsCount}
                    </div>
                    <div className="reservations-detail-container">
                      <img src={tableNumberImg} alt="" width="20px" />{" "}
                      {item.touchSaleTables
                        .map((table) => table.tableName)
                        .join(", ")}
                    </div>
                    <div className="reservations-detail-container">
                      <img src={timeLeftImg} alt="" width="20px" />
                      {timeRemaining[item.reservationId] || "--:--"}
                    </div>
                  </div>
                </div>

                <div className="reservations-seated-button-container">
                  {(item.status !== 3 || item !== undefined) && (
                    <Popconfirm
                      disabled={!checkIfCanSeated(item) && item.status === 1}
                      title={
                        item.status === 1
                          ? "Сигурни ли сте, че искате да настаните тази резервация?"
                          : "Сигурни ли сте, че искате да върнете тази резервация в Предстоящи?"
                      }
                      onConfirm={() => {
                        if (item.status === 1) {
                          return handleSeatedReservation(item);
                        } else {
                          return handleReturnToFutureReservation(item);
                        }
                      }}
                      placement="bottom"
                      okText="Да"
                      cancelText="Не"
                    >
                      <Button
                        id="reservations-seated-button"
                        size="large"
                        type="primary"
                        disabled={!checkIfCanSeated(item) && item.status === 1}
                        icon={
                          item.status === 1 ? (
                            <RightCircleTwoTone style={{ fontSize: "25px" }} />
                          ) : (
                            <LeftCircleTwoTone style={{ fontSize: "25px" }} />
                          )
                        }
                      ></Button>
                    </Popconfirm>
                  )}
                </div>
              </div>
              {item.note && (
                <>
                  <Divider style={{ margin: "5px" }} />
                  <span>- {item.note}</span>
                </>
              )}
            </Card>
          </List.Item>
        )}
      />
    </ConfigProvider>
  );
};

export default ReservationListCards;
